import React, { useState, useEffect } from 'react';
import Udesk from 'Udesk';
import {
    Table,
    Form,
    Input,
    Button,
    DatePicker,
    Space,
    Switch,
    message,
    Modal,
    Select,
} from 'udesk-ui';
import UdeskPagination from 'Component/common/udesk-pagination';
import type { ColumnsType } from 'udesk-ui/es/table';
import { putIntelligentPartnerCustomerPersonasManageRoleEnableById } from 'src/api/intelligentPartnerCustomerPersonas/manage/roleEnable/{id}';
import { deleteIntelligentPartnerCustomerPersonasById } from 'src/api/intelligentPartnerCustomerPersonas/{id}';
import { IntelligentPartnerCustomerPersonaFoundResponse } from 'src/api/types';
import { useSdkOptions } from 'src/util/hook';
import UdeskLocales from 'UdeskLocales';

const RoleManage: React.FC<any> = ({ history, routes, route, location }) => {
    const sdkOptions = useSdkOptions();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState<IntelligentPartnerCustomerPersonaFoundResponse[]>(
        []
    );
    const [searchParams, setSearchParams] = useState<any>({});
    const [form] = Form.useForm();
    // 分页配置
    const [paging, setPaging] = useState({
        pageNum: 1,
        pageSize: 10,
        total: 0,
    });
    const VOICE = [
        {
            label: /* 艾夏（自然） */ UdeskLocales['current'].pages.coach.templateManage.roleManage
                .list.aishaNature,
            value: 'aixia',
            robotTimbre: 0,
        },
        {
            label: /* 知莎（聊天） */ UdeskLocales['current'].pages.coach.templateManage.roleManage
                .list.zhishachat,
            value: 'zhistella',
            robotTimbre: 0,
        },
        {
            label: /* 知倩（温和） */ UdeskLocales['current'].pages.coach.templateManage.roleManage
                .list.zhiqianGentle,
            value: 'zhiqian',
            robotTimbre: 0,
        },
        {
            label: /* 知米（情感） */ UdeskLocales['current'].pages.coach.templateManage.roleManage
                .list.knowRiceemotion,
            value: 'zhimi_emo',
            robotTimbre: 0,
        },
        {
            label: /* 艾硕（自然） */ UdeskLocales['current'].pages.coach.templateManage.roleManage
                .list.aishuoNature,
            value: 'aishuo',
            robotTimbre: 1,
        },
        {
            label: /* 知硕（聊天） */ UdeskLocales['current'].pages.coach.templateManage.roleManage
                .list.zhishuochat,
            value: 'zhishuo',
            robotTimbre: 1,
        },
        {
            label: /* 艾浩（亲和） */ UdeskLocales['current'].pages.coach.templateManage.roleManage
                .list.aiHaoAffinity,
            value: 'aihao',
            robotTimbre: 1,
        },
        {
            label: /* 知祥（情感） */ UdeskLocales['current'].pages.coach.templateManage.roleManage
                .list.zhixiangEmotion,
            value: 'zhixiang',
            robotTimbre: 1,
        },
    ];

    // 表格列配置
    const columns: ColumnsType<IntelligentPartnerCustomerPersonaFoundResponse> = [
        {
            title: /* 角色名称 */ UdeskLocales['current'].pages.coach.templateManage.roleManage.list
                .roleName,
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: /* 角色性别 */ UdeskLocales['current'].pages.coach.templateManage.roleManage.list
                .roleGender,
            dataIndex: 'robotTimbre',
            key: 'robotTimbre',
            render: (timbre) => {
                return timbre === 1
                    ? /* 男 */ UdeskLocales['current'].pages.coach.templateManage.roleManage.list
                          .male
                    : /* 女 */ UdeskLocales['current'].pages.coach.templateManage.roleManage.list
                          .female;
            },
        },
        // {
        //     title: '角色类型',
        //     dataIndex: 'customerPersonaType',
        //     key: 'customerPersonaType',
        //     render: (type) => {
        //         // 根据注释 1:ai 2:客户画像
        //         return type === 1 ? 'AI' : '客户画像';
        //     },
        // },
        {
            title: /* 创建时间 */ UdeskLocales['current'].pages.coach.templateManage.roleManage.list
                .creationTime,
            dataIndex: 'createTime',
            key: 'createTime',
        },
        {
            title: /* 背景描述 */ UdeskLocales['current'].pages.coach.templateManage.roleManage.list
                .backgroundDescription,
            dataIndex: 'desc',
            key: 'desc',
            ellipsis: true,
        },

        {
            title: /* 音色配置 */ UdeskLocales['current'].pages.coach.templateManage.roleManage.list
                .timbreConfiguration,
            dataIndex: 'robotTimbreConfig',
            key: 'robotTimbreConfig',
            render: (_, item) => {
                return VOICE.find((v) => v.value === _)?.label;
            },
        },

        {
            title: /* 备注 */ UdeskLocales['current'].pages.coach.templateManage.roleManage.list
                .remarks,
            dataIndex: 'remark',
            key: 'remark',
            ellipsis: true,
        },
        {
            title: /* 状态 */ UdeskLocales['current'].pages.coach.templateManage.roleManage.list
                .status,
            dataIndex: 'enableStatus',
            key: 'enableStatus',
            render: (status: number, record) => (
                <Switch
                    checked={status === 0}
                    onChange={(checked) => handleStatusChange(checked, record)}
                />
            ),
        },
        {
            title: /* 操作 */ UdeskLocales['current'].pages.coach.templateManage.roleManage.list
                .operation,
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button type="link" onClick={() => handleEdit(record)}>
                        {/* 编辑 */}
                        {UdeskLocales['current'].pages.coach.templateManage.roleManage.list.edit}
                    </Button>
                    <Button type="link" danger onClick={() => handleDelete(record)}>
                        {/* 删除 */}
                        {UdeskLocales['current'].pages.coach.templateManage.roleManage.list.delete}
                    </Button>
                </Space>
            ),
        },
    ];

    // 获取列表数据
    const fetchList = async () => {
        setLoading(true);
        try {
            const url = Udesk.business.apiPath.concatApiPath(
                `/intelligentPartnerCustomerPersonas/manage/findList?pageNum=${paging.pageNum}&pageSize=${paging.pageSize}`,
                sdkOptions
            );
            const params: any = {
                ...searchParams,
            };
            if (searchParams?.dateRange) {
                params.startTime = searchParams.dateRange[0].format('YYYY-MM-DD') + 'T00:00:00';
                params.endTime = searchParams.dateRange[1].format('YYYY-MM-DD') + 'T23:59:59';
            }
            delete params.dateRange;
            await Udesk.ajax.post(url, params).then(
                (resp) => {
                    setDataSource(resp.data || []);
                    setPaging(resp.paging as any);
                },
                (reason) => {}
            );
        } catch (error) {
            message.error(
                /* 获取列表失败 */ UdeskLocales['current'].pages.coach.templateManage.roleManage
                    .list.failedToGetList
            );
        } finally {
            setLoading(false);
        }
    };

    // 分页变化
    const pageChanged = (pageNum: number, pageSize: number) => {
        setPaging((prev) => ({
            ...prev,
            pageNum,
            pageSize,
        }));
    };

    // 搜索
    const handleSearch = () => {
        setPaging({
            ...paging,
            pageNum: 1,
        });
        setSearchParams(form.getFieldsValue());
    };

    // 重置搜索
    const handleReset = () => {
        form.resetFields();
        setPaging({
            ...paging,
            pageNum: 1,
        });
        setSearchParams({});
    };

    // 状态切换
    const handleStatusChange = async (
        checked: boolean,
        record: IntelligentPartnerCustomerPersonaFoundResponse
    ) => {
        try {
            setLoading(true);
            await putIntelligentPartnerCustomerPersonasManageRoleEnableById(
                {
                    enableStatus: checked ? 0 : 1,
                } as any,
                {
                    segments: {
                        id: record.id!,
                    },
                }
            );
            Udesk.ui.notify.success(
                /* 更新状态成功 */ UdeskLocales['current'].pages.coach.templateManage.roleManage
                    .list.updateStatusSuccessfully
            );
            fetchList();
        } catch (error: any) {
            Udesk.ui.notify.error(error.errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // 新建角色
    const handleCreate = () => {
        const routeOptions = {
            history: history,
            routeName: 'coachTemplateManageRoleNew',
        };
        Udesk.ui.routing.transitionTo(routeOptions);
    };

    // 编辑角色
    const handleEdit = (record: IntelligentPartnerCustomerPersonaFoundResponse) => {
        console.log('Edit record:', record);
        const routeOptions = {
            history: history,
            routeName: 'coachTemplateManageRoleEdit',
            pathParams: { id: record.id! },
            state: {
                record,
            },
        };
        Udesk.ui.routing.transitionTo(routeOptions);
    };

    // 删除角色
    const handleDelete = async (record: IntelligentPartnerCustomerPersonaFoundResponse) => {
        Modal.confirm({
            title: /* 是否确认删除？ */ UdeskLocales['current'].pages.coach.templateManage
                .roleManage.list.areYouSureYouWantToDelete,
            okText: /* 删除 */ UdeskLocales['current'].pages.coach.templateManage.roleManage.list
                .delete,
            okType: 'danger',
            cancelText:
                /* 取消 */ UdeskLocales['current'].pages.coach.templateManage.roleManage.list
                    .cancel,
            onOk: async () => {
                try {
                    await deleteIntelligentPartnerCustomerPersonasById({
                        segments: {
                            id: record.id!,
                        },
                    });
                    Udesk.ui.notify.success(
                        /* 删除成功 */ UdeskLocales['current'].pages.coach.templateManage.roleManage
                            .list.deletedSuccessfully
                    );
                    fetchList();
                } catch (error: any) {
                    Udesk.ui.notify.error(error.errorMsg);
                }
            },
        });
    };

    useEffect(() => {
        fetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paging.pageNum, paging.pageSize, searchParams]);

    return (
        <div className="role-manage">
            {/* 搜索区域 */}
            <div className="search-area">
                <Form form={form} layout="inline" onFinish={handleSearch}>
                    <Form.Item
                        name="name"
                        label={
                            /* 角色名称 */ UdeskLocales['current'].pages.coach.templateManage
                                .roleManage.list.roleName
                        }
                    >
                        <Input
                            placeholder={
                                /* 请输入角色名称 */ UdeskLocales['current'].pages.coach
                                    .templateManage.roleManage.list.pleaseEnterARoleName
                            }
                        />
                    </Form.Item>
                    <Form.Item
                        name="robotTimbre"
                        label={
                            /* 性别 */ UdeskLocales['current'].pages.coach.templateManage.roleManage
                                .list.gender
                        }
                    >
                        <Select
                            allowClear
                            placeholder={
                                /* 请选择 */ UdeskLocales['current'].pages.coach.templateManage
                                    .roleManage.list.pleaseSelect
                            }
                            style={{ width: 200 }}
                        >
                            <Select.Option value={1}>
                                {/* 男 */}
                                {
                                    UdeskLocales['current'].pages.coach.templateManage.roleManage
                                        .list.male
                                }
                            </Select.Option>
                            <Select.Option value={0}>
                                {/* 女 */}
                                {
                                    UdeskLocales['current'].pages.coach.templateManage.roleManage
                                        .list.female
                                }
                            </Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="remark"
                        label={
                            /* 备注 */ UdeskLocales['current'].pages.coach.templateManage.roleManage
                                .list.remarks
                        }
                    >
                        <Input
                            placeholder={
                                /* 请输入备注 */ UdeskLocales['current'].pages.coach.templateManage
                                    .roleManage.list.pleaseEnterComments
                            }
                        />
                    </Form.Item>
                    <Form.Item
                        name="dateRange"
                        label={
                            /* 创建时间 */ UdeskLocales['current'].pages.coach.templateManage
                                .roleManage.list.creationTime
                        }
                    >
                        <DatePicker.RangePicker />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button onClick={handleReset}>
                                {/* 重置 */}
                                {
                                    UdeskLocales['current'].pages.coach.templateManage.roleManage
                                        .list.reset
                                }
                            </Button>
                            <Button type="primary" onClick={handleSearch}>
                                {/* 筛选 */}
                                {
                                    UdeskLocales['current'].pages.coach.templateManage.roleManage
                                        .list.filter
                                }
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </div>

            {/* 表格区域 */}
            <div className="table-area">
                <div className="table-header">
                    <Space>
                        <Button type="primary" onClick={handleCreate}>
                            {/* 新建 */}
                            {UdeskLocales['current'].pages.coach.templateManage.roleManage.list.new}
                        </Button>
                    </Space>
                </div>

                <Table
                    loading={loading}
                    columns={columns}
                    dataSource={dataSource}
                    rowKey="id"
                    pagination={false} // 关闭默认分页
                />

                {/* 分页器 */}
                <div className="pagination-wrapper">
                    {dataSource.length > 0 ? (
                        <UdeskPagination
                            current={paging.pageNum}
                            pageSize={paging.pageSize}
                            total={paging.total}
                            onChange={pageChanged}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default RoleManage;
